// live/LiveObserve.tsx (CX4) - the Observe lens of the North Star §5 spine, now on the ONE cinematic renderer.
// A pro auto-plays a full $1/$3 hand (preflop → river) and the table plays it out action by action - chips
// out front, the sweep, the pot count-up, the award push - while the pro talks over it: the read, the
// decision, and the why. This is "watch a pro play a real hand and explain it," the flagship of Phase 11.
//
// It is the SAME <CinematicFelt> + narrator (narrateHand) that Diagnose and Solo render - the lens is a
// config, not a fork. Observe's config: WATCH (self-paced scrub controls + the running ledger, no halt - the
// whole hand plays) and the OBSERVE narration lens (the pro narrates his own line, hero decisions included).
//
// TB2 continuity is unchanged and lives above this surface: the hand is the SESSION's (App holds the table),
// `onNextHand` advances that session - same opponents, carried stacks, rotated button - and App remounts this
// surface on the new hand number (keyed on handId), which resets the felt's stepper cleanly.
import { useMemo } from 'react';
import { CinematicFelt } from '../components/CinematicFelt';
import { money } from '../engine/bridge';
import { streamFor } from '../cinematic/streamFor';
import { narrateHand } from '../cinematic/narrate';
import type { LiveHandModel } from '../engine/liveRunner';
import type { PlayerTag } from '../diagnose/data';
import type { ActionKind, ArchetypeId } from '@postflop/schema';

/** TB2: the hand comes from the SESSION (held in App), not from a deal this surface owns. `onNextHand`
 *  advances that session - same opponents, carried stacks, rotated button. The read-capture props are
 *  accepted for signature parity with the do-modes but unused: Observe watches a pro play, it never captures
 *  a read of yours or plays a decision. */
export interface LiveSurfaceProps {
  model: LiveHandModel;
  onNextHand: () => void;
  onLockRead?: (seat: number, tag: PlayerTag) => void;
  seatTags?: ReadonlyMap<number, ArchetypeId>;
  onConfirmReads?: (evidence: readonly { seat: number; archetypeId: ArchetypeId }[]) => void;
  onHeroDecision?: (stepIndex: number, action: ActionKind, amount?: string) => void;
}

export function LiveObserve({ model, onNextHand }: LiveSurfaceProps) {
  const stream = useMemo(() => streamFor(model), [model]);
  // OBSERVE lens: the pro plays hero's line and narrates all of it (his own decisions included).
  const beats = useMemo(() => narrateHand(stream, model, { lens: 'observe' }), [stream, model]);

  // the outcome, for the footer summary under the felt.
  const contested = model.terminal.players.filter((p) => !p.folded);
  const net = model.heroNetChips;

  return (
    <>
      {/* WATCH: self-paced, full controls + ledger, no halt - the whole hand plays out. */}
      <CinematicFelt stream={stream} beats={beats} controls />

      <div className="body">
        <div className="obsresult">
          <span className="ort">
            {contested.length >= 2 ? `Showdown, ${contested.length} players` : 'Taken uncontested'}
          </span>
          <span className={'orv' + (net >= 0 ? ' up' : ' down')}>
            {net >= 0 ? `Pro won ${money(net)}` : `Pro lost ${money(-net)}`}
          </span>
        </div>
      </div>

      <div className="dock">
        <div className="dockrow">
          <button type="button" className="cta" onClick={onNextHand}>
            Watch another hand
          </button>
        </div>
      </div>
    </>
  );
}
